/**
 * Ordersystem 程序库：localStorage、事件总线、菜单/订单、打印小票、管理端配置
 * 依赖：ui-labels.bundle.js + js/constants.js → app-receipt-data → data-lib → order-lib → config-lib → config.js → cart-logic →
 * cart-service → api.js → print-lib → app-print → 本文件；顾客页再由 constants-loader 加载 html-templates + ui-controller。
 */
(function (global) {
  'use strict';

  const orderLib = global.RestaurantOrderLib;
  const configLib = global.RestaurantConfigLib;
  const RC = global.RestaurantAppConfig;
  const API = global.RestaurantApiService;
  if (!orderLib) {
    throw new Error('[Ordersystem] 请先在 app.js 之前加载 js/order-lib.js');
  }
  if (!configLib) {
    throw new Error('[Ordersystem] 请先在 app.js 之前加载 js/config-lib.js');
  }
  if (!RC || !API) {
    throw new Error('[Ordersystem] 请先在 app.js 之前加载 js/config.js 与 js/api.js（RestaurantApiService）');
  }

  const KEYS = RC.buildKeys(orderLib);

  const OS_LAN_IPV4 = RC.OS_LAN_IPV4;
  const getDefaultThermalPrintEndpoint = RC.getDefaultThermalPrintEndpoint;

  const CUSTOMER_SESSION_STORAGE = RC.CUSTOMER_SESSION_STORAGE;
  const ORDER_COOLDOWN_UNTIL_KEY = RC.ORDER_COOLDOWN_UNTIL_KEY;
  const CUSTOMER_MEAL_TAB_KEY = RC.CUSTOMER_MEAL_TAB_KEY;

  const DEFAULT_CUSTOMER_GUEST_NAME = RC.DEFAULT_CUSTOMER_GUEST_NAME;
  const GUEST_NAME_MAX_LEN = RC.GUEST_NAME_MAX_LEN;
  const DEFAULT_CUSTOMER_TERMINAL_TYPE = RC.DEFAULT_CUSTOMER_TERMINAL_TYPE || 'ipad_mini';

  /**
   * Safari/iPad 无痕或 ITP 下 sessionStorage 可能不可用或 setItem 抛错；用内存 JSON 回退，避免登录后 getCustomerSession 为空又被 showLogin 打回。
   * 刷新页面后会丢失（与无存储环境一致）。
   */
  let customerSessionStorageFallbackJson = null;

  /** 顾客端：写入订单/徽章用的终端类型（≤32 字符）；无法识别时以 ipad_mini 为门店默认基准 */
  function detectCustomerTerminalType() {
    try {
      const ua = navigator.userAgent || '';
      const maxTouch = Number(navigator.maxTouchPoints) || 0;
      const platform = String(navigator.platform || '');
      const isIpad =
        /\biPad\b/i.test(ua) || (platform === 'MacIntel' && maxTouch > 1);
      if (isIpad) return /\biPad\b/i.test(ua) && /mini/i.test(ua) ? 'ipad_mini' : 'ipad';
      if (/\biPhone\b/i.test(ua) || /\biPod\b/i.test(ua)) return 'iphone';
      if (/\bAndroid\b/i.test(ua)) return /\bMobile\b/i.test(ua) ? 'android_phone' : 'android_tablet';
      const coarse = globalThis.matchMedia?.('(pointer: coarse)').matches === true;
      const fine = globalThis.matchMedia?.('(pointer: fine)').matches === true;
      const sw = Math.min(global.screen?.width || 0, global.screen?.height || 0);
      if (coarse && sw >= 600) return 'ipad_mini';
      if (fine && !coarse) return 'desktop';
      return DEFAULT_CUSTOMER_TERMINAL_TYPE;
    } catch {
      return DEFAULT_CUSTOMER_TERMINAL_TYPE;
    }
  }

  function refreshCustomerTerminalTypeInSession() {
    const o = readCustomerSessionRaw();
    if (!o || typeof o.table !== 'string' || !String(o.table).trim()) return;
    const next = detectCustomerTerminalType();
    if (String(o.terminalType || '') === next) return;
    mergeCustomerSessionPartial({ terminalType: next });
  }

  function buildGuestSlotLabels(table, guests) {
    let t = String(table ?? '').trim() || '?';
    const maxTableLen = GUEST_NAME_MAX_LEN - 3;
    if (t.length > maxTableLen) t = t.slice(0, maxTableLen);
    const n = Math.min(Math.max(1, parseInt(String(guests), 10) || 1), 99);
    const out = [];
    for (let i = 0; i < n; i++) {
      const suffix = i < 26 ? String.fromCharCode(65 + i) : String(i + 1);
      let label = t + suffix;
      if (label.length > GUEST_NAME_MAX_LEN) label = label.slice(0, GUEST_NAME_MAX_LEN);
      out.push(label);
    }
    return out;
  }

  /** 去掉空格后匹配，兼容曾用「桌号 后缀」与当前「桌号+后缀」连写 */
  function compactGuestLabelKey(s) {
    return String(s ?? '').replace(/\s/g, '');
  }

  function resolveGuestSlotLabel(labels, raw) {
    const s = raw != null ? String(raw).trim().slice(0, GUEST_NAME_MAX_LEN) : '';
    if (!s) return '';
    if (labels.includes(s)) return s;
    const c = compactGuestLabelKey(s);
    return labels.find((l) => compactGuestLabelKey(l) === c) || '';
  }

  function normalizeGuestCarts(raw) {
    if (!raw || typeof raw !== 'object') return {};
    const out = {};
    for (const [slot, cart] of Object.entries(raw)) {
      if (!cart || typeof cart !== 'object') continue;
      const inner = {};
      for (const [dishId, qty] of Object.entries(cart)) {
        const q = parseInt(String(qty), 10);
        if (Number.isFinite(q) && q > 0) inner[String(dishId)] = q;
      }
      out[String(slot).slice(0, GUEST_NAME_MAX_LEN)] = inner;
    }
    return out;
  }

  function pickGuestNameForTable(table, guests, rawPreferred) {
    const labels = buildGuestSlotLabels(table, guests);
    if (!labels.length) return DEFAULT_CUSTOMER_GUEST_NAME;
    const s =
      rawPreferred != null ? String(rawPreferred).trim().slice(0, GUEST_NAME_MAX_LEN) : '';
    const resolved = s ? resolveGuestSlotLabel(labels, s) : '';
    if (resolved) return resolved;
    if (s.length === 1) {
      const u = s.toUpperCase();
      const hit = labels.find((l) => l.length && l[l.length - 1] === u);
      if (hit) return hit;
    }
    return labels[0];
  }

  function resolveGuestFromUrlParam(param, table, guests) {
    const labels = buildGuestSlotLabels(table, guests);
    if (param == null || !String(param).trim()) return labels[0];
    const p = String(param).trim().slice(0, GUEST_NAME_MAX_LEN);
    const byCompact = resolveGuestSlotLabel(labels, p);
    if (byCompact) return byCompact;
    const u = p.toUpperCase();
    if (u.length === 1 && u >= 'A' && u <= 'Z') {
      const hit = labels.find((l) => l.length && l[l.length - 1] === u);
      if (hit) return hit;
    }
    const idx = parseInt(p, 10);
    if (Number.isFinite(idx) && idx >= 0 && idx < labels.length) return labels[idx];
    return pickGuestNameForTable(table, guests, p);
  }

  function readCustomerSessionRaw() {
    try {
      if (customerSessionStorageFallbackJson != null) {
        const o = JSON.parse(customerSessionStorageFallbackJson);
        return o && typeof o === 'object' ? o : null;
      }
      const s = global.sessionStorage && global.sessionStorage.getItem(CUSTOMER_SESSION_STORAGE);
      if (!s) return null;
      const o = JSON.parse(s);
      return o && typeof o === 'object' ? o : null;
    } catch {
      return null;
    }
  }

  function writeCustomerSessionRaw(o) {
    const str = JSON.stringify(o);
    try {
      if (global.sessionStorage) {
        global.sessionStorage.setItem(CUSTOMER_SESSION_STORAGE, str);
        customerSessionStorageFallbackJson = null;
        return;
      }
    } catch (e) {
      console.warn('[Ordersystem] sessionStorage 写入失败，使用内存会话（仅本页有效）', e);
    }
    customerSessionStorageFallbackJson = str;
  }

  function mergeCustomerSessionPartial(partial) {
    const cur = readCustomerSessionRaw();
    if (!cur || typeof cur.table !== 'string') return;
    writeCustomerSessionRaw(Object.assign({}, cur, partial));
  }

  function getCustomerSession() {
    try {
      const o = readCustomerSessionRaw();
      if (!o || typeof o.table !== 'string' || !String(o.table).trim()) return null;
      const guests = parseInt(String(o.guests), 10);
      if (!Number.isFinite(guests) || guests < 1 || guests > 99) return null;
      const or = parseInt(String(o.orderRound ?? 0), 10);
      const table = String(o.table).trim();
      const labels = buildGuestSlotLabels(table, guests);
      const guestName = pickGuestNameForTable(
        table,
        guests,
        o.guestName != null ? o.guestName : DEFAULT_CUSTOMER_GUEST_NAME
      );
      const terminalType =
        o.terminalType != null && String(o.terminalType).trim()
          ? String(o.terminalType).trim().slice(0, 32)
          : DEFAULT_CUSTOMER_TERMINAL_TYPE;
      const carts = normalizeGuestCarts(o.carts);
      for (const k of Object.keys(carts).slice()) {
        const canon = resolveGuestSlotLabel(labels, k);
        if (!canon || canon === k) continue;
        if (carts[canon] == null) carts[canon] = carts[k];
        delete carts[k];
      }
      for (const k of Object.keys(carts)) {
        if (!labels.includes(k)) delete carts[k];
      }
      const assignedStartRaw = parseInt(String(o.assignedStartIndex ?? 0), 10);
      const assignedGuestsRaw = parseInt(String(o.assignedGuests ?? guests), 10);
      const assignedStartIndex = Number.isFinite(assignedStartRaw)
        ? Math.min(labels.length - 1, Math.max(0, assignedStartRaw))
        : 0;
      const assignedGuests = Number.isFinite(assignedGuestsRaw)
        ? Math.min(labels.length - assignedStartIndex, Math.max(1, assignedGuestsRaw))
        : labels.length;
      const assignedGuestSlots = labels.slice(assignedStartIndex, assignedStartIndex + assignedGuests);
      return {
        table,
        guests,
        guestName,
        guestSlots: labels,
        assignedStartIndex,
        assignedGuests,
        assignedGuestSlots,
        orderRound: Number.isFinite(or) && or >= 0 ? or : 0,
        terminalType,
        carts,
      };
    } catch {
      return null;
    }
  }

  function clearOrderCooldown() {
    try {
      if (global.sessionStorage) global.sessionStorage.removeItem(ORDER_COOLDOWN_UNTIL_KEY);
    } catch (e) {
      console.warn(e);
    }
  }

  function setCustomerSession(table, guests, guestName, assigned) {
    clearOrderCooldown();
    const t = String(table).trim();
    const g = Number(guests);
    const gn = pickGuestNameForTable(t, g, guestName);
    const labels = buildGuestSlotLabels(t, g);
    const assignedStartRaw = parseInt(String(assigned?.startIndex ?? 0), 10);
    const assignedGuestsRaw = parseInt(String(assigned?.guests ?? assigned?.addGuests ?? g), 10);
    const assignedStartIndex = Number.isFinite(assignedStartRaw)
      ? Math.min(labels.length - 1, Math.max(0, assignedStartRaw))
      : 0;
    const assignedGuests = Number.isFinite(assignedGuestsRaw)
      ? Math.min(labels.length - assignedStartIndex, Math.max(1, assignedGuestsRaw))
      : labels.length;
    writeCustomerSessionRaw({
      table: t,
      guests: g,
      guestName: gn,
      assignedStartIndex,
      assignedGuests,
      orderRound: 0,
      terminalType: detectCustomerTerminalType(),
      carts: {},
    });
  }

  /** 切换同桌内当前客人账户：写入上一人购物车快照并返回下一人的购物车快照 */
  function applyCustomerGuestSwitch(fromGuest, currentCartRecord, toGuest) {
    const o = readCustomerSessionRaw();
    if (!o || typeof o.table !== 'string' || !String(o.table).trim()) return null;
    const table = String(o.table).trim();
    const guests = parseInt(String(o.guests), 10);
    const labels = buildGuestSlotLabels(table, guests);
    const toResolved = resolveGuestSlotLabel(labels, toGuest);
    if (!toResolved) return null;
    const carts = normalizeGuestCarts(o.carts);
    let from = '';
    if (String(fromGuest || '').trim()) from = resolveGuestSlotLabel(labels, fromGuest);
    if (!from) from = resolveGuestSlotLabel(labels, o.guestName);
    if (from && labels.includes(from)) {
      const rec = currentCartRecord && typeof currentCartRecord === 'object' ? currentCartRecord : {};
      carts[from] = { ...rec };
    }
    o.carts = carts;
    o.guestName = toResolved;
    writeCustomerSessionRaw(o);
    const loaded =
      carts[toResolved] && typeof carts[toResolved] === 'object' ? carts[toResolved] : {};
    return { ...loaded };
  }

  /** 将当前客人的购物车写入会话（用于 pagehide 等） */
  function persistCustomerGuestCart(guestName, cartRecord) {
    const o = readCustomerSessionRaw();
    if (!o || typeof o.table !== 'string') return;
    const table = String(o.table).trim();
    const guests = parseInt(String(o.guests), 10);
    const labels = buildGuestSlotLabels(table, guests);
    const g = resolveGuestSlotLabel(labels, guestName);
    if (!g) return;
    const carts = normalizeGuestCarts(o.carts);
    carts[g] = cartRecord && typeof cartRecord === 'object' ? { ...cartRecord } : {};
    mergeCustomerSessionPartial({ carts });
  }

  /** 整台发单后清空同桌各客人购物车快照 */
  function clearAllCustomerGuestCarts() {
    const o = readCustomerSessionRaw();
    if (!o || typeof o.table !== 'string') return;
    const guests = parseInt(String(o.guests), 10);
    if (!Number.isFinite(guests) || guests < 1) return;
    const labels = buildGuestSlotLabels(String(o.table).trim(), guests);
    const carts = {};
    labels.forEach((l) => {
      carts[l] = {};
    });
    mergeCustomerSessionPartial({ carts });
  }

  function clearCustomerGuestCarts(slotNames) {
    const o = readCustomerSessionRaw();
    if (!o || typeof o.table !== 'string') return;
    const guests = parseInt(String(o.guests), 10);
    if (!Number.isFinite(guests) || guests < 1) return;
    const labels = buildGuestSlotLabels(String(o.table).trim(), guests);
    const carts = normalizeGuestCarts(o.carts);
    const slots = Array.isArray(slotNames) ? slotNames : [];
    slots.forEach((slot) => {
      const g = resolveGuestSlotLabel(labels, slot);
      if (g) carts[g] = {};
    });
    mergeCustomerSessionPartial({ carts });
  }

  /** 多台设备同桌同步：忽略本机 WS 回显时用于比对 */
  function getTableSyncClientId() {
    try {
      const k = 'ordersystemTableSyncClientId';
      let id = global.sessionStorage && global.sessionStorage.getItem(k);
      if (!id) {
        id = 'ts_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
        if (global.sessionStorage) global.sessionStorage.setItem(k, id);
      }
      return id;
    } catch {
      return 'ts_' + Math.random().toString(36).slice(2);
    }
  }

  /**
   * 应用服务端或 WS 推送的同桌购物车快照（仅当 table 与当前会话一致）。
   * @returns {boolean} 是否已写入 sessionStorage
   */
  function applyRemoteTableCartSync(payload) {
    if (!payload || typeof payload !== 'object') return false;
    const tableRemote = String(payload.table ?? '').trim();
    if (!tableRemote) return false;
    const o = readCustomerSessionRaw();
    if (!o || typeof o.table !== 'string' || String(o.table).trim() !== tableRemote) return false;
    const guestsRemote = parseInt(String(payload.guests), 10);
    if (!Number.isFinite(guestsRemote) || guestsRemote < 1 || guestsRemote > 99) return false;
    const labels = buildGuestSlotLabels(tableRemote, guestsRemote);
    const cartsIn = normalizeGuestCarts(payload.carts);
    const carts = {};
    labels.forEach((l) => {
      carts[l] = cartsIn[l] && typeof cartsIn[l] === 'object' ? { ...cartsIn[l] } : {};
    });
    const or = parseInt(String(payload.orderRound ?? 0), 10);
    const orderRound = Number.isFinite(or) && or >= 0 ? or : 0;
    const prevGn = String(o.guestName || '').trim();
    const guestName = pickGuestNameForTable(tableRemote, guestsRemote, prevGn);
    writeCustomerSessionRaw({
      ...o,
      guests: guestsRemote,
      carts,
      orderRound,
      guestName,
    });
    return true;
  }

  function clearCustomerSession() {
    clearOrderCooldown();
    customerSessionStorageFallbackJson = null;
    try {
      if (global.sessionStorage) global.sessionStorage.removeItem(CUSTOMER_SESSION_STORAGE);
    } catch (e) {
      console.warn(e);
    }
  }

  /** 顾客本会话已成功下单次数（用于厨房小票「点餐轮数」）；与 session 中其它字段一并持久化 */
  function setCustomerOrderRound(savedRound) {
    if (!readCustomerSessionRaw()) return;
    const n = parseInt(String(savedRound), 10);
    const orderRound = Number.isFinite(n) && n >= 0 ? n : 0;
    mergeCustomerSessionPartial({ orderRound });
  }

  function setOrderCooldownMinutes(minutes) {
    const m = parseInt(String(minutes), 10);
    if (!Number.isFinite(m) || m <= 0) {
      clearOrderCooldown();
      return;
    }
    try {
      if (global.sessionStorage)
        global.sessionStorage.setItem(ORDER_COOLDOWN_UNTIL_KEY, String(Date.now() + m * 60 * 1000));
    } catch (e) {
      console.warn(e);
    }
  }

  function getOrderCooldownRemainingMs() {
    try {
      let lockM = 0;
      try {
        const s = configLib.getSystemSettings();
        const raw = s.lockMinutesBetweenOrders;
        lockM = parseInt(String(raw != null ? raw : 0), 10);
      } catch {
        lockM = 0;
      }
      if (!Number.isFinite(lockM) || lockM < 0) lockM = 0;

      if (!global.sessionStorage) return 0;
      const v = global.sessionStorage.getItem(ORDER_COOLDOWN_UNTIL_KEY);
      if (!v) return 0;
      const until = parseInt(v, 10);
      if (!Number.isFinite(until)) {
        clearOrderCooldown();
        return 0;
      }
      const remain = Math.max(0, until - Date.now());
      if (remain <= 0) {
        clearOrderCooldown();
        return 0;
      }
      /**
       * 勿在「间隔为 0 / hydrate 尚未写入锁分钟」时抢先 clear：否则会抹掉刚写入的 sessionStorage，
       * 顾客端表现为无倒计时弹窗。
       */
      if (lockM <= 0) return remain;
      return remain;
    } catch {
      return 0;
    }
  }

  function getCustomerMealTab() {
    try {
      const s = configLib.getSystemSettings();
      const o = s.customerMealOverride != null ? String(s.customerMealOverride).trim() : '';
      if (o === 'lunch' || o === 'dinner' || o === 'special') return o;
    } catch {
      /* ignore */
    }
    try {
      if (!global.sessionStorage) return 'lunch';
      const v = global.sessionStorage.getItem(CUSTOMER_MEAL_TAB_KEY);
      if (v === 'lunch' || v === 'dinner' || v === 'special') return v;
    } catch {
      /* ignore */
    }
    return 'lunch';
  }

  function setCustomerMealTab(tab) {
    if (tab !== 'lunch' && tab !== 'dinner' && tab !== 'special') return;
    try {
      if (global.sessionStorage) global.sessionStorage.setItem(CUSTOMER_MEAL_TAB_KEY, tab);
    } catch (e) {
      console.warn(e);
    }
  }

  const UI_LANG_ALLOWED = configLib.UI_LANG_ALLOWED;

  const LANG_PICKER = RC.LANG_PICKER;

  const UI_LABELS = RC.getUiLabelsBundle();

  const _receiptBundle = RC.getReceiptDataBundle();
  const RECEIPT_LOCALE = _receiptBundle.RECEIPT_LOCALE;
  const RECEIPT_LABELS = _receiptBundle.RECEIPT_LABELS;
  const RECEIPT_FONT_CSS = _receiptBundle.RECEIPT_FONT_CSS;

  function receiptT(lang, key) {
    const L = RECEIPT_LABELS[lang] || RECEIPT_LABELS.zh;
    const z = RECEIPT_LABELS.zh;
    const e = RECEIPT_LABELS.en;
    return L[key] || e[key] || z[key] || key;
  }

  function adminLabelAtLang(lang, key) {
    const pack = UI_LABELS[lang] || UI_LABELS.zh;
    const z = UI_LABELS.zh;
    return pack[key] || z[key] || key;
  }

  function receiptLabelDual(lang1, lang2, key) {
    const a = receiptT(lang1, key);
    if (!lang2 || lang2 === lang1) return a;
    return a + ' / ' + receiptT(lang2, key);
  }

  function adminLabelDual(lang1, lang2, key) {
    const a = adminLabelAtLang(lang1, key);
    if (!lang2 || lang2 === lang1) return a;
    return a + ' / ' + adminLabelAtLang(lang2, key);
  }

  function findDishInMenu(menu, dishId) {
    if (dishId == null) return null;
    const idStr = String(dishId);
    return (menu.dishes || []).find((x) => String(x.id) === idStr || x.id === dishId) || null;
  }

  /** 按小票语言从菜单取菜品名；无翻译时回退 fallback（通常为订单里已存名称）。 */
  function dishNameForReceiptLang(menu, dishId, lang, fallback) {
    const d = findDishInMenu(menu, dishId);
    const fb = fallback != null && String(fallback).trim() ? String(fallback) : '';
    if (!d) return fb;
    if (lang === 'zh') {
      if (d.t && d.t.zh && d.t.zh.n) return String(d.t.zh.n);
      if (d.name) return String(d.name);
      if (d.t && d.t.en && d.t.en.n) return String(d.t.en.n);
      return fb;
    }
    const tr = d.t && d.t[lang];
    if (tr && tr.n) return String(tr.n);
    if (d.name) return String(d.name);
    return fb;
  }

  function formatDishNameForUi(name) {
    const s = String(name ?? '').trim();
    if (!s) return '';
    const hasUpper = /[A-ZÄÖÜ]/.test(s);
    const hasLower = /[a-zäöüß]/.test(s);
    if (!hasUpper || hasLower) return s;
    return s
      .toLocaleLowerCase('de-DE')
      .replace(/(^|[\s\-\/([{])([a-zäöüß])/g, (_m, prefix, ch) => prefix + ch.toLocaleUpperCase('de-DE'));
  }

  /**
   * 顾客端 UI：按当前界面语言取菜品名（与 dishNameForReceiptLang 规则一致）。
   */
  function dishNameForUiLang(menu, dish) {
    if (!dish) return '';
    const lang = UI_LANG_ALLOWED.includes(getUiLang()) ? getUiLang() : 'de';
    return formatDishNameForUi(dishNameForReceiptLang(menu, dish.id, lang, dish.name));
  }

  /**
   * 顾客端 UI：按当前界面语言取描述；优先 d.t[lang].d，避免界面已为中文却仍显示单语 description 里的德文等。
   */
  function dishDescriptionForUiLang(menu, dish) {
    if (!dish) return '';
    const lang = UI_LANG_ALLOWED.includes(getUiLang()) ? getUiLang() : 'de';
    const d = typeof dish.id !== 'undefined' ? findDishInMenu(menu, dish.id) || dish : dish;
    const fb = d.description != null && String(d.description).trim() ? String(d.description) : '';
    if (lang === 'zh') {
      if (d.t && d.t.zh && d.t.zh.d) return String(d.t.zh.d);
      if (d.t && d.t.en && d.t.en.d) return String(d.t.en.d);
      if (fb) return fb;
      return '';
    }
    const tr = d.t && d.t[lang];
    if (tr && tr.d) return String(tr.d);
    if (fb) return fb;
    return '';
  }

  /**
   * 当前语言下的「描述/备注」，**不读 t.zh.d**（避免与后台「注解」字段混在同一条链里导致德语等环境下逻辑难推理）。
   */
  function dishLocalizedBlurbExcludingZhNote(menu, dish) {
    if (!dish) return '';
    const lang = UI_LANG_ALLOWED.includes(getUiLang()) ? getUiLang() : 'de';
    const d = typeof dish.id !== 'undefined' ? findDishInMenu(menu, dish.id) || dish : dish;
    const fb = d.description != null && String(d.description).trim() ? String(d.description).trim() : '';
    if (lang === 'zh') {
      if (d.t && d.t.en && d.t.en.d) return String(d.t.en.d).trim();
      return fb;
    }
    const tr = d.t && d.t[lang];
    if (tr && tr.d != null && String(tr.d).trim()) return String(tr.d).trim();
    return fb;
  }

  /**
   * 仅当前界面语言下的 **t[lang].d**（中文界面用 t.en.d），**不含**顶层 `description`。
   * 用于详情里已有后台注解 t.zh.d 时追加「另一语种说明」，避免历史 description 与现行注解叠行。
   */
  function dishTranslatedDescriptionOnly(menu, dish) {
    if (!dish) return '';
    const lang = UI_LANG_ALLOWED.includes(getUiLang()) ? getUiLang() : 'de';
    const d = typeof dish.id !== 'undefined' ? findDishInMenu(menu, dish.id) || dish : dish;
    if (lang === 'zh') {
      if (d.t && d.t.en && d.t.en.d != null && String(d.t.en.d).trim()) return String(d.t.en.d).trim();
      return '';
    }
    const tr = d.t && d.t[lang];
    if (tr && tr.d != null && String(tr.d).trim()) return String(tr.d).trim();
    return '';
  }

  /**
   * 详情弹窗「注解」：后台字段 **t.zh.d** 在任意界面语言下都展示；若另有当前语言描述且内容不同，则第二行追加。
   */
  /** 管理端 `foodClass === 'veg'` 时顾客端素菜样式（菜名/注解绿色） */
  function dishFoodClassIsVeg(dish) {
    if (!dish || dish.foodClass == null) return false;
    return String(dish.foodClass).trim() === 'veg';
  }

  /** 管理端菜品库 `foodClass`：veg / meat / fish → 当前界面语言短标签 */
  function dishFoodClassLabelForUi(dish) {
    if (!dish || dish.foodClass == null) return '';
    const fc = String(dish.foodClass).trim();
    if (fc === 'veg') return uiT('customer.dishFoodClassVeg');
    if (fc === 'meat') return uiT('customer.dishFoodClassMeat');
    if (fc === 'fish') return uiT('customer.dishFoodClassFish');
    return '';
  }

  function dishDetailNoteForUi(menu, dish) {
    if (!dish) return '';
    const d = typeof dish.id !== 'undefined' ? findDishInMenu(menu, dish.id) || dish : dish;
    const zhRaw = d.t && d.t.zh && d.t.zh.d != null ? String(d.t.zh.d) : '';
    const zhNote = zhRaw.replace(/\uFEFF/g, '').trim();
    const extra = String(
      (zhNote ? dishTranslatedDescriptionOnly(menu, d) : dishLocalizedBlurbExcludingZhNote(menu, d)) ?? ''
    ).trim();
    const extraDedup = extra && extra !== zhNote ? extra : '';
    if (zhNote && extraDedup) return zhNote + '\n' + extraDedup;
    if (zhNote) return zhNote;
    if (extraDedup) return extraDedup;
    return '';
  }

  /** 与后台 DISH_EU_ALLERGENS id 一致；顾客端展示用德文欧盟用语（与存盘 id 对应） */
  const DISH_EU_ALLERGENS_UI = [
    { id: 'eu1', labelDe: 'Glutenhaltiges Getreide' },
    { id: 'eu2', labelDe: 'Krebstiere und daraus gewonnene Erzeugnisse' },
    { id: 'eu3', labelDe: 'Eier und daraus gewonnene Erzeugnisse' },
    { id: 'eu4', labelDe: 'Fische und daraus gewonnene Erzeugnisse' },
    { id: 'eu5', labelDe: 'Erdnüsse und daraus gewonnene Erzeugnisse' },
    { id: 'eu6', labelDe: 'Sojabohnen und daraus gewonnene Erzeugnisse' },
    { id: 'eu7', labelDe: 'Milch und daraus gewonnene Erzeugnisse' },
    { id: 'eu8', labelDe: 'Schalenfrüchte, namentlich Mandeln' },
    { id: 'eu9', labelDe: 'Sellerie und daraus gewonnene Erzeugnisse' },
    { id: 'eu10', labelDe: 'Senf und daraus gewonnene Erzeugnisse' },
    { id: 'eu11', labelDe: 'Sesamsamen und daraus gewonnene Erzeugnisse' },
    { id: 'eu12', labelDe: 'Schwefeldioxid und Sulphite' },
    { id: 'eu13', labelDe: 'Lupinen und daraus gewonnene Erzeugnisse' },
    { id: 'eu14', labelDe: 'Weichtiere und daraus gewonnene Erzeugnisse' },
  ];

  /** @returns {string[]} */
  function dishAllergenLabelsForUi(dish) {
    if (!dish) return [];
    const raw = dish.allergenIds;
    const ids = Array.isArray(raw) ? raw.map((x) => String(x)) : [];
    if (!ids.length) return [];
    const set = new Set(ids);
    return DISH_EU_ALLERGENS_UI.filter((a) => set.has(a.id)).map((a) => a.labelDe);
  }

  function receiptTicketUpper(s) {
    return String(s == null ? '' : s).toUpperCase();
  }

  /** 数量 + 两空格 + 餐名（第二语言时「名1 名2」，与厨房热敏行一致）。 */
  function formatReceiptItemLineString(menu, it, rlang, rlangSecondary) {
    const did = it.dishId != null ? it.dishId : it.id;
    const n1 = dishNameForReceiptLang(menu, did, rlang, it.name);
    let namePart = n1;
    if (rlangSecondary) {
      const n2 = dishNameForReceiptLang(menu, did, rlangSecondary, it.name);
      if (n2 && n2 !== n1) namePart = n1 + ' ' + n2;
    }
    const q = it.quantity != null ? it.quantity : it.q != null ? it.q : 0;
    return String(q) + '  ' + namePart;
  }

  /**
   * 紧凑小票：第 1 行台号+顾客姓名（大写），随后每行「数量 + 两空格 + 餐名」（大写），末行日期时间（小写）。
   * receiptCfg 须含 receiptLang、receiptLangSecondary（与 getMergedReceiptConfig 一致）。
   */
  function buildCompactReceiptPayload(orderData, receiptCfg) {
    const rlang = UI_LANG_ALLOWED.includes(receiptCfg.receiptLang) ? receiptCfg.receiptLang : 'de';
    const rlang2 = normalizeReceiptLangSecondary(rlang, receiptCfg.receiptLangSecondary);
    const bi = !!rlang2;
    const menu = getMenu();
    const loc = RECEIPT_LOCALE[rlang] || 'zh-CN';

    const tblRaw = orderData.table != null && String(orderData.table).trim() ? String(orderData.table).trim() : '';
    const gnameRaw = orderData.guestName != null ? String(orderData.guestName).trim() : '';
    const guestU = receiptTicketUpper(gnameRaw || DEFAULT_CUSTOMER_GUEST_NAME);
    const tableU = tblRaw ? receiptTicketUpper(tblRaw) : '';
    const line1 = receiptTicketUpper((tableU ? tableU + ' ' : '') + guestU).trim();

    const items = orderData.items || [];
    const itemLines = items.map((it) =>
      receiptTicketUpper(formatReceiptItemLineString(menu, it, rlang, bi ? rlang2 : ''))
    );

    const atRaw = orderData.at ? new Date(orderData.at).toLocaleString(loc) : new Date().toLocaleString(loc);
    const timeLine = String(atRaw).toLowerCase();

    return { line1, itemLines, timeLine, timeLocale: loc };
  }

  function normalizeReceiptLangSecondary(primary, secondary) {
    const p = UI_LANG_ALLOWED.includes(primary) ? primary : 'de';
    const s = typeof secondary === 'string' && UI_LANG_ALLOWED.includes(secondary) ? secondary : '';
    if (!s || s === p) return '';
    return s;
  }

  function buildReceiptPrintPayload(receiptCfg) {
    const r1 = UI_LANG_ALLOWED.includes(receiptCfg.receiptLang) ? receiptCfg.receiptLang : 'de';
    const r2 = normalizeReceiptLangSecondary(r1, receiptCfg.receiptLangSecondary);
    return {
      bilingual: !!r2,
      timeLocale: RECEIPT_LOCALE[r1] || 'zh-CN',
      labelOrderId: receiptLabelDual(r1, r2, 'orderId') + ':',
      labelTime: receiptLabelDual(r1, r2, 'time') + ':',
      labelTable: adminLabelDual(r1, r2, 'admin.orderTableLabel') + ':',
      labelGuests: adminLabelDual(r1, r2, 'admin.orderGuestsLabel'),
      labelCategory: receiptLabelDual(r1, r2, 'categoryZone') + ':',
      labelTotal: receiptLabelDual(r1, r2, 'total') + ':',
      labelGuestName: receiptLabelDual(r1, r2, 'guestCall') + ':',
      quantityLabel: receiptLabelDual(r1, r2, 'quantityLabel'),
      thanks: receiptLabelDual(r1, r2, 'thanks'),
    };
  }

  function getUiLang() {
    return configLib.getUiLang();
  }

  function setUiLang(code) {
    configLib.setUiLang(code);
  }

  function uiT(key) {
    const lang = getUiLang();
    const pack = UI_LABELS[lang];
    const z = UI_LABELS.zh;
    if (pack && pack[key]) return pack[key];
    if (z[key]) return z[key];
    return key;
  }

  /** 预设分类 ID 走 menu.category.<id>；否则用存储的 name。 */
  function categoryLabelForUi(cat) {
    if (!cat || typeof cat.id !== 'string' || !cat.id) return '';
    const key = 'menu.category.' + cat.id;
    const lang = getUiLang();
    const pack = UI_LABELS[lang];
    const z = UI_LABELS.zh;
    if (pack && pack[key]) return pack[key];
    if (z[key]) return z[key];
    const nm = cat.name != null ? String(cat.name).trim() : '';
    return nm || cat.id;
  }

  /** 打印机配置里展示的名称：内置默认档按界面语言翻译存储名 Default printer / 旧版 默认打印机 */
  function printerProfileDisplayName(p) {
    if (!p) return '';
    if (p.id === DEFAULT_BUILTIN_PRINTER_ID && isBuiltinDefaultPrinterStoredName(p.name)) {
      return uiT('admin.builtinPrinterDefaultName');
    }
    const nm = p.name != null ? String(p.name).trim() : '';
    return nm || p.id;
  }

  function normalizePrinterNameInputForSave(printerId, rawInput) {
    const t = String(rawInput == null ? '' : rawInput).trim();
    if (printerId !== DEFAULT_BUILTIN_PRINTER_ID) return t || 'Printer';
    if (!t || isBuiltinDefaultPrinterStoredName(t)) return STORED_BUILTIN_PRINTER_NAME;
    for (let i = 0; i < UI_LANG_ALLOWED.length; i++) {
      const pack = UI_LABELS[UI_LANG_ALLOWED[i]];
      if (pack && pack['admin.builtinPrinterDefaultName'] === t) return STORED_BUILTIN_PRINTER_NAME;
    }
    return t;
  }

  function applyUiLabels(root) {
    const r = root || document;
    const lang = getUiLang();
    const htmlLang =
      lang === 'zh'
        ? 'zh-CN'
        : lang === 'ja'
          ? 'ja'
          : lang === 'tr'
            ? 'tr'
            : lang === 'de'
              ? 'de'
              : lang === 'fr'
                ? 'fr'
                : lang;
    if (document.documentElement) document.documentElement.setAttribute('lang', htmlLang);
    r.querySelectorAll('[data-ui]').forEach((el) => {
      const k = el.getAttribute('data-ui');
      if (k) el.textContent = uiT(k);
    });
    r.querySelectorAll('[data-ui-placeholder]').forEach((el) => {
      const k = el.getAttribute('data-ui-placeholder');
      if (k) el.setAttribute('placeholder', uiT(k));
    });
    r.querySelectorAll('[data-ui-aria]').forEach((el) => {
      const k = el.getAttribute('data-ui-aria');
      if (k) el.setAttribute('aria-label', uiT(k));
    });
    r.querySelectorAll('[data-ui-title]').forEach((el) => {
      const k = el.getAttribute('data-ui-title');
      if (k) el.setAttribute('title', uiT(k));
    });
  }

  function updateLangSwitcherUI(prefix) {
    const lang = getUiLang();
    function syncBtn(btnId) {
      const btn = document.getElementById(btnId);
      if (!btn) return;
      const base = btnId.replace(/-lang-btn$/, '');
      const img = document.getElementById(base + '-lang-flag');
      const abbr = document.getElementById(base + '-lang-abbr');
      if (!img || !abbr) return;
      const row = RC.LANG_PICKER.find((x) => x.code === lang);
      if (row) {
        img.src = row.flag;
        img.alt = row.abbr;
        abbr.textContent = row.abbr;
      }
    }
    if (prefix === 'customer') {
      syncBtn('customer-workbar-lang-btn');
      const sel = document.getElementById('customer-login-lang-select');
      if (sel) {
        const v = getUiLang();
        if ([...sel.options].some((o) => o.value === v)) sel.value = v;
      }
    } else {
      syncBtn(prefix + '-lang-btn');
    }
  }

  const DEFAULT_RESTAURANT_INFO = configLib.DEFAULT_RESTAURANT_INFO;

  function newPrinterId() {
    return 'p_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 9);
  }

  const STORED_BUILTIN_PRINTER_NAME = RC.STORED_BUILTIN_PRINTER_NAME;
  const DEFAULT_BUILTIN_PRINTER_ID = RC.DEFAULT_BUILTIN_PRINTER_ID;

  function isBuiltinDefaultPrinterStoredName(name) {
    const s = String(name == null ? '' : name).trim();
    return s === STORED_BUILTIN_PRINTER_NAME || s === 'Default printer';
  }

  const DEFAULT_PRINT_V2 = RC.DEFAULT_PRINT_V2;

  function normalizePrintConfig(raw) {
    const base = JSON.parse(JSON.stringify(DEFAULT_PRINT_V2));
    if (!raw || typeof raw !== 'object') return base;
    if (Array.isArray(raw.printers) && raw.printers.length) {
      base.printers = raw.printers.map((p, i) => {
        const id = typeof p.id === 'string' && p.id ? p.id : newPrinterId();
        let name = p.name != null && String(p.name).trim() !== '' ? String(p.name).trim() : '';
        if (!name) name = id === DEFAULT_BUILTIN_PRINTER_ID ? STORED_BUILTIN_PRINTER_NAME : '打印机 ' + (i + 1);
        else if (id === DEFAULT_BUILTIN_PRINTER_ID && isBuiltinDefaultPrinterStoredName(name)) {
          name = STORED_BUILTIN_PRINTER_NAME;
        }
        return {
          id,
          name,
          paperWidth: p.paperWidth === '58' ? '58' : '80',
          autoPrint: !!p.autoPrint,
          header: p.header == null ? '' : String(p.header),
          footer: p.footer == null ? '' : String(p.footer),
        };
      });
      const aid = typeof raw.activePrinterId === 'string' ? raw.activePrinterId : '';
      base.activePrinterId = base.printers.some((x) => x.id === aid) ? aid : base.printers[0].id;
      if (raw.orderReceipt && typeof raw.orderReceipt === 'object') {
        const rl = raw.orderReceipt.receiptLang;
        base.orderReceipt.receiptLang = UI_LANG_ALLOWED.includes(rl) ? rl : 'de';
        const rs = raw.orderReceipt.receiptLangSecondary;
        base.orderReceipt.receiptLangSecondary =
          typeof rs === 'string' && UI_LANG_ALLOWED.includes(rs) && rs !== base.orderReceipt.receiptLang ? rs : '';
        const fp = raw.orderReceipt.fontPreset;
        base.orderReceipt.fontPreset = ['mono', 'sans', 'large'].includes(fp) ? fp : 'mono';
      }
      return base;
    }
    const id = DEFAULT_BUILTIN_PRINTER_ID;
    let legacyName = raw.name == null ? '' : String(raw.name).trim();
    if (!legacyName) legacyName = STORED_BUILTIN_PRINTER_NAME;
    if (isBuiltinDefaultPrinterStoredName(legacyName)) legacyName = STORED_BUILTIN_PRINTER_NAME;
    base.printers = [
      {
        id,
        name: legacyName,
        paperWidth: raw.paperWidth === '58' ? '58' : '80',
        autoPrint: !!raw.autoPrint,
        header: raw.header == null ? '' : String(raw.header),
        footer: raw.footer == null ? '' : String(raw.footer),
      },
    ];
    base.activePrinterId = id;
    return base;
  }

  function getMergedReceiptConfig(fullCfg, printerId) {
    const cfg = fullCfg || normalizePrintConfig(null);
    const pid =
      (printerId && cfg.printers.some((x) => x.id === printerId) ? printerId : null) ||
      (cfg.activePrinterId && cfg.printers.some((x) => x.id === cfg.activePrinterId) ? cfg.activePrinterId : null) ||
      cfg.printers[0].id;
    const p = cfg.printers.find((x) => x.id === pid) || cfg.printers[0];
    const orc = cfg.orderReceipt || DEFAULT_PRINT_V2.orderReceipt;
    const rl = UI_LANG_ALLOWED.includes(orc.receiptLang) ? orc.receiptLang : 'de';
    const rs = normalizeReceiptLangSecondary(rl, orc.receiptLangSecondary);
    const fp = ['mono', 'sans', 'large'].includes(orc.fontPreset) ? orc.fontPreset : 'mono';
    return {
      paperWidth: p.paperWidth,
      header: p.header,
      footer: p.footer,
      autoPrint: p.autoPrint,
      printerId: p.id,
      printerName: p.name,
      receiptLang: rl,
      receiptLangSecondary: rs,
      fontPreset: fp,
    };
  }

  function saveToDB(key, value) {
    try {
      const str = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, str);
    } catch (e) {
      console.warn('localStorage 不可用:', e);
    }
  }

  function getFromDB(key, defaultValue) {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null || raw === '') return defaultValue;
      try {
        return JSON.parse(raw);
      } catch {
        return raw;
      }
    } catch (e) {
      console.warn('localStorage 读取失败:', e);
      return defaultValue;
    }
  }

  function SimpleEmitter() {
    const map = Object.create(null);
    const api = {
      on(ev, fn) {
        if (!map[ev]) map[ev] = [];
        map[ev].push(fn);
        return () => api.off(ev, fn);
      },
      off(ev, fn) {
        const a = map[ev];
        if (!a) return;
        map[ev] = a.filter((x) => x !== fn);
      },
      emit(ev, payload) {
        const list = map[ev] || [];
        for (let i = 0; i < list.length; i++) {
          try {
            list[i](payload);
          } catch (e) {
            console.error(e);
          }
        }
      },
    };
    return api;
  }

  const emitter = SimpleEmitter();

  global.addEventListener('restaurantMenuUpdated', (e) => {
    if (e && e.detail) emitter.emit('menu:updated', e.detail);
  });
  global.addEventListener('restaurantOrdersUpdated', (e) => {
    if (e && e.detail) emitter.emit('orders:updated', e.detail);
  });
  global.addEventListener('restaurantInfoUpdated', (e) => {
    if (e && e.detail) emitter.emit('restaurant:updated', e.detail);
  });
  global.addEventListener('restaurantSystemSettingsUpdated', (e) => {
    if (e && e.detail) emitter.emit('system:updated', e.detail);
  });

  const menuLib = global.RestaurantDataLib;
  if (!menuLib) {
    throw new Error('[Ordersystem] 请先在 app.js 之前加载 js/data-lib.js');
  }

  /** 空菜单结构占位（无示例数据）；真实餐牌来自后台保存的 menu.json */
  const DEMO_MENU = menuLib.getDefaultMenu();

  function ensureMenuInitialized() {
    return menuLib.ensureInitialized();
  }

  async function hydrateDefaultsFromServerIfNeeded() {
    const proto = global.location && global.location.protocol;
    if (proto !== 'http:' && proto !== 'https:') return;

    const revKey = menuLib.MENU_SERVER_REV_KEY || 'restaurantMenuServerRev';

    let syncMenu = false;
    let syncRestaurant = false;
    try {
      const sp = new URL(global.location.href).searchParams;
      syncMenu = sp.get('syncMenu') === '1' || sp.get('syncMenu') === 'true';
      syncRestaurant = sp.get('syncRestaurant') === '1' || sp.get('syncRestaurant') === 'true';
    } catch (_) {}

    if (syncMenu) {
      try {
        global.localStorage.removeItem(menuLib.STORAGE_KEY);
        global.localStorage.removeItem(revKey);
      } catch (_) {}
    }
    if (syncRestaurant) {
      try {
        global.localStorage.removeItem(configLib.KEYS.RESTAURANT_INFO);
      } catch (_) {}
    }
    if (syncMenu || syncRestaurant) {
      try {
        const u = new URL(global.location.href);
        u.searchParams.delete('syncMenu');
        u.searchParams.delete('syncRestaurant');
        const qs = u.searchParams.toString();
        global.history.replaceState({}, '', u.pathname + (qs ? '?' + qs : '') + u.hash);
      } catch (_) {}
    }

    const baseHref = new URL('./', global.location.href).href;

    try {
      const rset = await fetch(new URL('customer-system-settings.json', baseHref).href, { cache: 'no-store' });
      if (rset.ok) {
        const data = await rset.json();
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          const cur = configLib.getSystemSettings();
          configLib.saveSystemSettings({ ...cur, ...data });
        }
      }
    } catch (_) {
      /* 无文件或离线 */
    }

    try {
      const info = getRestaurantInfo();
      const nm = info && info.name != null ? String(info.name).trim() : '';
      if (syncRestaurant || !nm || nm === '鲜点餐厅') {
        try {
          const rr = await fetch(new URL('default-restaurant.json', baseHref).href, { cache: 'no-store' });
          if (rr.ok) {
            const data = await rr.json();
            if (data && typeof data === 'object') saveRestaurantInfo({ ...getRestaurantInfo(), ...data });
          }
        } catch (_) {}
      }
    } catch (_) {}

    const readLocalServerRev = () => {
      try {
        const n = parseInt(String(global.localStorage.getItem(revKey) || ''), 10);
        return Number.isFinite(n) ? n : 0;
      } catch (_) {
        return 0;
      }
    };
    const writeLocalServerRev = (v) => {
      try {
        global.localStorage.setItem(revKey, String(v));
      } catch (_) {}
    };

    // ?syncMenu=1：清缓存后强制以 menu.json 为准
    if (syncMenu) {
      try {
        const r = await fetch(new URL('menu.json', baseHref).href, { cache: 'no-store' });
        if (r.ok) {
          const data = await r.json();
          const m = menuLib.normalizeMenu(data);
          if (m) {
            menuLib.setMenu(m);
            const sr =
              data && typeof data.menuSavedRev === 'number' && Number.isFinite(data.menuSavedRev)
                ? data.menuSavedRev
                : 0;
            writeLocalServerRev(sr);
          }
        }
      } catch (_) {}
      return;
    }

    // 仅当服务器版本更新时才覆盖本地；无 menuSavedRev 的旧版空文件不覆盖本地已有餐品
    try {
      const r = await fetch(new URL('menu.json', baseHref).href, { cache: 'no-store' });
      if (!r.ok) return;
      const data = await r.json();
      const m = menuLib.normalizeMenu(data);
      if (!m) return;
      const serverRev =
        data && typeof data.menuSavedRev === 'number' && Number.isFinite(data.menuSavedRev)
          ? data.menuSavedRev
          : null;
      const localRev = readLocalServerRev();
      const localDishCount = (menuLib.getMenu().dishes || []).length;
      const serverDishCount = (m.dishes || []).length;
      if (serverRev == null) {
        if (serverDishCount === 0 && localDishCount > 0) return;
        menuLib.setMenu(m);
        writeLocalServerRev(0);
        return;
      }
      if (serverRev > localRev) {
        menuLib.setMenu(m);
        writeLocalServerRev(serverRev);
      }
    } catch (_) {}
  }

  function getMenu() {
    return menuLib.getMenu();
  }

  function setMenu(menu) {
    menuLib.setMenu(menu);
  }

  function getOrders() {
    return orderLib.getOrders();
  }

  function saveOrders(list) {
    orderLib.saveOrders(list);
  }

  function addOrder(order) {
    orderLib.addOrder(order);
  }

  function getPrintConfig() {
    const d = getFromDB(KEYS.PRINT_CONFIG, null);
    return normalizePrintConfig(d && typeof d === 'object' ? d : null);
  }

  function savePrintConfig(partial) {
    const cur = getPrintConfig();
    if (!partial || typeof partial !== 'object') {
      saveToDB(KEYS.PRINT_CONFIG, cur);
      return cur;
    }
    const next = {
      ...cur,
      ...partial,
      printers: partial.printers != null ? partial.printers : cur.printers,
      orderReceipt: partial.orderReceipt
        ? { ...cur.orderReceipt, ...partial.orderReceipt }
        : cur.orderReceipt,
    };
    if (!Array.isArray(next.printers) || !next.printers.length) {
      const fallback = normalizePrintConfig(null);
      next.printers = fallback.printers;
      next.activePrinterId = fallback.activePrinterId;
    }
    if (!next.printers.some((p) => p.id === next.activePrinterId)) {
      next.activePrinterId = next.printers[0].id;
    }
    saveToDB(KEYS.PRINT_CONFIG, next);
    return next;
  }

  function getNetworkConfig() {
    return configLib.getNetworkConfig();
  }

  function saveNetworkConfig(partial) {
    return configLib.saveNetworkConfig(partial);
  }

  const PRINTER_MAPPING_EXAMPLE = RC.PRINTER_MAPPING_EXAMPLE;

  const DEFAULT_PRINTER_ROUTING = RC.DEFAULT_PRINTER_ROUTING;

  function normalizePrinterRouting(raw) {
    const base = {
      dispatchMode: DEFAULT_PRINTER_ROUTING.dispatchMode,
      categoryToPrinterId: {},
    };
    if (!raw || typeof raw !== 'object') return base;
    if (raw.dispatchMode === 'per_dish' || raw.dispatchMode === 'per_category') {
      base.dispatchMode = raw.dispatchMode;
    }
    if (raw.categoryToPrinterId && typeof raw.categoryToPrinterId === 'object') {
      Object.keys(raw.categoryToPrinterId).forEach((k) => {
        const v = raw.categoryToPrinterId[k];
        if (v != null && String(v).trim()) base.categoryToPrinterId[k] = String(v).trim();
      });
    }
    return base;
  }

  function getPrinterRouting() {
    const d = getFromDB(KEYS.PRINTER_ROUTING, null);
    return normalizePrinterRouting(d);
  }

  function savePrinterRouting(partial) {
    if (!partial || typeof partial !== 'object') return getPrinterRouting();
    const cur = getPrinterRouting();
    const next = {
      dispatchMode:
        partial.dispatchMode === 'per_dish' || partial.dispatchMode === 'per_category'
          ? partial.dispatchMode
          : cur.dispatchMode,
      categoryToPrinterId:
        partial.replaceAll === true && partial.categoryToPrinterId && typeof partial.categoryToPrinterId === 'object'
          ? (function () {
              const o = {};
              Object.keys(partial.categoryToPrinterId).forEach((k) => {
                const v = partial.categoryToPrinterId[k];
                if (v != null && String(v).trim()) o[k] = String(v).trim();
              });
              return o;
            })()
          : { ...cur.categoryToPrinterId },
    };
    if (!partial.replaceAll && partial.categoryToPrinterId && typeof partial.categoryToPrinterId === 'object') {
      Object.keys(partial.categoryToPrinterId).forEach((k) => {
        const v = partial.categoryToPrinterId[k];
        if (v == null || !String(v).trim()) delete next.categoryToPrinterId[k];
        else next.categoryToPrinterId[k] = String(v).trim();
      });
    }
    saveToDB(KEYS.PRINTER_ROUTING, next);
    return next;
  }

  if (typeof global.__OS_createPrintModule !== 'function') {
    console.error('[Ordersystem] 请先在 app.js 之前加载 app-print.js');
  }
  /** 热敏 POST：按 window.location.hostname（及 port），局域网/iPad 打开谁的 IP 就请求谁，不写死固定局域网地址 */
  const thermalPrintEndpointFromPageHost = () => {
    try {
      const loc = global.location;
      if (loc?.hostname) {
        const proto = loc.protocol === 'https:' ? 'https:' : 'http:';
        const port = loc.port || (proto === 'https:' ? '443' : '3000');
        return `${proto}//${loc.hostname}:${port}/print`;
      }
    } catch {
      /* ignore */
    }
    return getDefaultThermalPrintEndpoint();
  };
  const _print =
    typeof global.__OS_createPrintModule === 'function'
      ? global.__OS_createPrintModule({
          KEYS,
          getFromDB,
          saveToDB,
          getMenu,
          getPrintConfig,
          getPrinterRouting,
          getMergedReceiptConfig,
          UI_LANG_ALLOWED,
          GUEST_NAME_MAX_LEN,
          defaultPrintEndpoint: thermalPrintEndpointFromPageHost(),
          receipt: {
            normalizeReceiptLangSecondary,
            dishNameForReceiptLang,
            buildReceiptPrintPayload,
            buildCompactReceiptPayload,
          },
        })
      : null;

  if (!_print) {
    throw new Error('[Ordersystem] 须在 app.js 前按顺序加载 app-print.js');
  }

  const {
    dispatchOrder,
    postPrintDispatch,
    getPrinterSettings,
    savePrinterSettings,
    processOrderPrinting,
  } = _print;

  /** 见 js/api.js（RestaurantApiService） */
  const postCustomerOrderToBackend = API.postCustomerOrder;
  const sendThermalOrderViaPrintLib = API.sendThermalOrderViaPrintLib;
  const pingPrintServerHealth = API.pingPrintServerHealth;

  /** 与顾客端 POST /api/orders 同源：用于同步「清台」到 Node 与其它终端 */
  function pickCustomerApiBaseUrlForSync() {
    try {
      const raw = configLib.getNetworkConfig();
      const b = raw && raw.apiBaseUrl != null ? String(raw.apiBaseUrl).trim() : '';
      if (b) return b.replace(/\/$/, '');
    } catch (_) {}
    try {
      if (global.location && /^https?:$/i.test(String(global.location.protocol || ''))) {
        return String(global.location.origin || '').replace(/\/$/, '');
      }
    } catch (_) {}
    return '';
  }

  /**
   * Beenden / 清台：从本机 restaurantOrders 移除该桌全部订单，并 POST /api/table-history-cleared
   *（WebSocket 通知管理端与其它 iPad 同步；服务端同时修剪 orders-log.json）。
   */
  function clearRestaurantOrdersForTableAndBroadcast(tableRaw) {
    const table = tableRaw != null ? String(tableRaw).trim() : '';
    if (!table) return;
    try {
      const list = orderLib.getOrders() || [];
      const next = list.filter((o) => {
        const t = o && o.table != null ? String(o.table).trim() : '';
        return t !== table;
      });
      if (next.length !== list.length) {
        orderLib.saveOrders(next);
      }
    } catch (e) {
      console.warn('[Ordersystem] clearRestaurantOrdersForTableAndBroadcast', e);
    }
    const base = pickCustomerApiBaseUrlForSync();
    if (!base) return;
    void API.postJson(`${base}/api/table-history-cleared`, { table }, { timeoutMs: 15000, maxRetries: 1 });
  }

  function getRestaurantInfo() {
    return configLib.getRestaurantInfo();
  }

  function saveRestaurantInfo(partial) {
    return configLib.saveRestaurantInfo(partial);
  }

  const DEFAULT_SYSTEM_SETTINGS = configLib.DEFAULT_SYSTEM_SETTINGS;

  function getSystemSettings() {
    return configLib.getSystemSettings();
  }

  function systemSettingsUseMealProfiles(s) {
    return configLib.systemSettingsUseMealProfiles(s);
  }

  /** 仅按配置 id 交集筛选；用于切换有效时段，不做回退 */
  function filterDishesByMealProfileStrict(fullMenu, mealKey, s) {
    if (!fullMenu || !fullMenu.dishes) return [];
    const ids =
      mealKey === 'dinner' ? s.menuDinnerIds : mealKey === 'special' ? s.menuSpecialIds : s.menuLunchIds;
    const set = new Set(configLib.normalizeMenuProfileIdArray(ids));
    if (!set.size) return [];
    return (fullMenu.dishes || []).filter((d) => d && set.has(String(d.id)));
  }

  /** 顾客端展示：有交集则严格；未配置 id 或配置与菜单脱节时退回整本菜单，避免无菜/无分类 */
  function filterDishesByMealProfile(fullMenu, mealKey, s) {
    if (!fullMenu || !fullMenu.dishes) return [];
    const all = (fullMenu.dishes || []).filter(Boolean);
    const strict = filterDishesByMealProfileStrict(fullMenu, mealKey, s);
    if (strict.length) return strict;
    const ids =
      mealKey === 'dinner' ? s.menuDinnerIds : mealKey === 'special' ? s.menuSpecialIds : s.menuLunchIds;
    const set = new Set(configLib.normalizeMenuProfileIdArray(ids));
    if (!set.size) return all;
    return all;
  }

  function saveSystemSettings(partial) {
    return configLib.saveSystemSettings(partial);
  }

  /**
   * 从服务器重新拉取 customer-system-settings.json 并合并进 localStorage。
   * 用于下单前刷新 lockMinutesBetweenOrders 等关键字段，避免 iOS 长时间开屏、
   * visibilitychange 不稳定导致 admin 端刚改的锁屏时长没同步到客户端。
   * 成功/失败都会 resolve（失败时 localStorage 不动），调用方照常继续。
   */
  async function refreshSystemSettingsFromServer() {
    try {
      const baseHref = new URL('./', global.location.href).href;
      const rset = await fetch(new URL('customer-system-settings.json', baseHref).href, {
        cache: 'no-store',
      });
      if (!rset.ok) return false;
      const data = await rset.json();
      if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
      const cur = configLib.getSystemSettings();
      configLib.saveSystemSettings({ ...cur, ...data });
      return true;
    } catch (_) {
      return false;
    }
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** 顾客端媒体 URL：菜单若在电脑上用 localhost 保存，手机用局域网 IP 打开时仍指向本机，需按当前页 origin 重写 */
  function resolveCustomerMediaUrl(url) {
    const raw = url != null ? String(url).trim() : '';
    if (!raw) return '';
    if (/^data:/i.test(raw) || /^blob:/i.test(raw)) return raw;
    if (typeof location === 'undefined' || !location?.href) return raw;
    try {
      const u = new URL(raw, location.href);
      const h = u.hostname.toLowerCase().replace(/^\[|\]$/g, '');
      if (h === 'localhost' || h === '127.0.0.1' || h === '::1') {
        const cur = new URL(location.href);
        u.protocol = cur.protocol;
        u.host = cur.host;
        return u.href;
      }
    } catch (_) {
      /* ignore */
    }
    return raw;
  }

  function nlToBr(s) {
    return escapeHtml(s).replace(/\n/g, '<br/>');
  }

  function buildReceiptHtml(orderData, printCfg) {
    const full = getPrintConfig();
    const base = getMergedReceiptConfig(full, printCfg && printCfg.printerId);
    const cfg = Object.assign({}, base, printCfg && typeof printCfg === 'object' ? printCfg : {});
    const rlang = UI_LANG_ALLOWED.includes(cfg.receiptLang) ? cfg.receiptLang : 'de';
    const fp = RECEIPT_FONT_CSS[cfg.fontPreset] ? cfg.fontPreset : 'mono';
    const fontCss = RECEIPT_FONT_CSS[fp];
    const loc = RECEIPT_LOCALE[rlang] || 'zh-CN';
    const htmlLang = loc;
    const compact = buildCompactReceiptPayload(orderData, cfg);
    const pw = cfg.paperWidth === '58' ? '58mm' : '80mm';
    const L = (k) => escapeHtml(receiptT(rlang, k));
    const oid = escapeHtml(orderData.id || '');
    const docTitle = L('docTitle') + ' ' + oid;
    const printBtn = L('printBtn');
    const headExtra = cfg.header ? `<div class="extra top">${nlToBr(cfg.header)}</div>` : '';
    const footExtra = cfg.footer ? `<div class="extra bottom">${nlToBr(cfg.footer)}</div>` : '';
    const itemHtml = compact.itemLines.map((line) => '<div class="r-item">' + escapeHtml(line) + '</div>').join('');
    const timeHtml = escapeHtml(compact.timeLine);

    return `<!DOCTYPE html><html lang="${htmlLang}"><head><meta charset="UTF-8"/><title>${docTitle}</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; padding: 12px; background: #eee; color: #111; ${fontCss} }
  .toolbar { text-align: center; margin-bottom: 12px; }
  .toolbar button { padding: 10px 20px; font: inherit; border: 0; border-radius: 8px; background: #d3071c; color: #fff; cursor: pointer; }
  .sheet { max-width: ${pw}; margin: 0 auto; background: #fff; padding: 10mm 4mm; box-shadow: 0 2px 12px rgba(0,0,0,.12); }
  .extra { line-height: 1.4; color: #333; margin: 6px 0; text-align: center; font-size: 9pt; }
  .extra.top { border-bottom: 1px dashed #ccc; padding-bottom: 8px; }
  .extra.bottom { border-top: 1px dashed #ccc; padding-top: 8px; margin-top: 10px; }
  .r-line1 { font-size: 13pt; line-height: 1.25; font-weight: 700; text-transform: uppercase; letter-spacing: 0.02em; text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .r-item { font-size: 10pt; line-height: 1.3; text-transform: uppercase; margin-top: 5px; word-break: break-word; }
  .r-time { font-size: 5pt; line-height: 1.25; text-transform: lowercase; margin-top: 12px; color: #333; }
  @page { size: ${pw} auto; margin: 3mm; }
  @media print {
    body { background: #fff; padding: 0; }
    .toolbar { display: none !important; }
    .sheet { max-width: none; width: ${pw}; margin: 0; padding: 2mm 2.5mm; box-shadow: none; }
    .r-line1 { font-size: 13pt; text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .r-item { font-size: 10pt; }
    .r-time { font-size: 5pt; }
  }
</style></head><body>
<div class="toolbar"><button type="button" id="p">${printBtn}</button></div>
<div class="sheet">
  ${headExtra}
  <div class="r-line1">${escapeHtml(compact.line1)}</div>
  ${itemHtml}
  <div class="r-time">${timeHtml}</div>
  ${footExtra}
</div>
<script>document.getElementById('p').onclick=function(){window.print();};<\/script>
</body></html>`;
  }

  function openReceiptWindow(html) {
    const w = global.open('', '_blank', 'noopener,noreferrer');
    if (!w) {
      alert('无法打开打印窗口，请允许弹窗。');
      return null;
    }
    w.document.write(html);
    w.document.close();
    return w;
  }

  function printOrderReceipt(orderData, printCfgOverride) {
    if (!orderData) return;
    const full = getPrintConfig();
    const merged = getMergedReceiptConfig(full, printCfgOverride && printCfgOverride.printerId);
    const cfg = Object.assign({}, merged, printCfgOverride || {});
    openReceiptWindow(buildReceiptHtml(orderData, cfg));
  }

  function printTestReceipt(printerId, fullCfgOverride) {
    const full = fullCfgOverride && fullCfgOverride.printers ? fullCfgOverride : getPrintConfig();
    const pid = printerId || full.activePrinterId;
    const merged = getMergedReceiptConfig(full, pid);
    const rn = getRestaurantInfo().name || 'YUOKI GmbH';
    const dummy = {
      id: 'TEST-' + Date.now(),
      at: new Date().toISOString(),
      restaurantName: rn + '（测试）',
      table: '1',
      guestCount: 2,
      guestName: '1A',
      items: [
        { name: '测试菜品 A', quantity: 2, price: 18 },
        { name: '测试饮品 B', quantity: 1, price: 12 },
      ],
      total: 48,
    };
    openReceiptWindow(buildReceiptHtml(dummy, merged));
  }

  function autoPrintLatestOrderIfEnabled() {
    const full = getPrintConfig();
    const ap = full.printers.find((p) => p.autoPrint);
    if (!ap) return;
    const merged = getMergedReceiptConfig(full, ap.id);
    const list = getOrders();
    if (!list.length) return;
    const latest = list[0];
    const key = 'restaurantLastAutoPrintId';
    const last = sessionStorage.getItem(key);
    if (last === latest.id) return;
    sessionStorage.setItem(key, latest.id);
    const w = openReceiptWindow(buildReceiptHtml(latest, merged));
    if (w) {
      w.addEventListener('load', function tryPrint() {
        setTimeout(() => {
          try {
            w.print();
          } catch (e) {
            /* 部分浏览器需用户手势 */
          }
        }, 400);
      });
    }
  }

  function pingApi(url) {
    return API.pingApi(url);
  }

  const OS = {
    KEYS,
    /** 与下方默认打印 URL 同步，供管理端或脚本读取；请改为本机 ipconfig 的 IPv4 */
    OS_LAN_IPV4,
    getDefaultThermalPrintEndpoint,
    PRINTER_MAPPING_EXAMPLE,
    saveToDB,
    getFromDB,
    emitter,
    menuLib,
    orderLib,
    configLib,
    DEMO_MENU,
    ensureMenuInitialized,
    ensureDemoMenu: ensureMenuInitialized,
    hydrateDefaultsFromServerIfNeeded,
    getMenu,
    setMenu,
    getOrders,
    saveOrders,
    addOrder,
    getPrintConfig,
    getMergedReceiptConfig,
    savePrintConfig,
    getPrinterRouting,
    savePrinterRouting,
    dispatchOrder,
    postPrintDispatch,
    getPrinterSettings,
    savePrinterSettings,
    processOrderPrinting,
    getNetworkConfig,
    saveNetworkConfig,
    getRestaurantInfo,
    saveRestaurantInfo,
    getSystemSettings,
    saveSystemSettings,
    refreshSystemSettingsFromServer,
    systemSettingsUseMealProfiles,
    filterDishesByMealProfile,
    filterDishesByMealProfileStrict,
    DEFAULT_SYSTEM_SETTINGS,
    setOrderCooldownMinutes,
    getOrderCooldownRemainingMs,
    clearOrderCooldown,
    getCustomerMealTab,
    setCustomerMealTab,
    printOrderReceipt,
    printTestReceipt,
    buildReceiptHtml,
    autoPrintLatestOrderIfEnabled,
    pingApi,
    resolveCustomerMediaUrl,
    postCustomerOrderToBackend,
    sendThermalOrderViaPrintLib,
    pingPrintServerHealth,
    escapeHtml,
    DEFAULT_CUSTOMER_GUEST_NAME,
    DEFAULT_CUSTOMER_TERMINAL_TYPE,
    detectCustomerTerminalType,
    refreshCustomerTerminalTypeInSession,
    buildGuestSlotLabels,
    resolveGuestFromUrlParam,
    applyCustomerGuestSwitch,
    persistCustomerGuestCart,
    clearAllCustomerGuestCarts,
    clearCustomerGuestCarts,
    getTableSyncClientId,
    applyRemoteTableCartSync,
    getCustomerSession,
    setCustomerSession,
    clearCustomerSession,
    clearRestaurantOrdersForTableAndBroadcast,
    setCustomerOrderRound,
    getUiLang,
    setUiLang,
    uiT,
    dishNameForReceiptLang,
    dishNameForUiLang,
    dishDescriptionForUiLang,
    dishDetailNoteForUi,
    dishFoodClassIsVeg,
    dishFoodClassLabelForUi,
    dishAllergenLabelsForUi,
    categoryLabelForUi,
    printerProfileDisplayName,
    normalizePrinterNameInputForSave,
    defaultBuiltinPrinterId: DEFAULT_BUILTIN_PRINTER_ID,
    applyUiLabels,
    updateLangSwitcherUI,
    LANG_PICKER,
    UI_LANG_ALLOWED,
  };

  global.Ordersystem = OS;

  if (global.OS && typeof global.OS.registerModule === 'function') {
    try {
      global.OS.registerModule('facade', OS);
      global.OS.facade = OS;
      global.OS.ready = true;
    } catch (_) {}
  }
})(typeof window !== 'undefined' ? window : globalThis);
